import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoryInvitePage({ user }) {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  const claimAttempted = useRef(false);

  const isLoggedIn = !!localStorage.getItem('noyo_token');

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API}/story-link/${linkId}`);
        setLinkInfo(res.data);
      } catch (e) {
        setError('This link is no longer valid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [linkId]);

  const handleClaim = async () => {
    const token = localStorage.getItem('noyo_token');
    if (!token) {
      localStorage.setItem('pending_story_invite', linkId);
      navigate('/signup');
      return;
    }
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/story-link/${linkId}/claim`);
      const folderId = res.data.folder_id;
      navigate(`/stories/${folderId}`, { replace: true });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save story';
      if (msg === 'Cannot claim your own story') {
        navigate('/stories', { replace: true });
      } else {
        setError(msg);
        setClaiming(false);
      }
    }
  };

  // Auto-claim if user is logged in
  useEffect(() => {
    if (isLoggedIn && linkInfo && !error && !claimAttempted.current) {
      claimAttempted.current = true;
      handleClaim();
    }
  }, [isLoggedIn, linkInfo]);

  if (loading || (isLoggedIn && linkInfo && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full text-center shadow-xl">
          <p className="text-foreground font-serif font-semibold mb-2">Link Expired</p>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button onClick={() => navigate('/signup')} className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm" data-testid="story-invite-signup">
            Go to NOYO
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-sm w-full text-center shadow-xl" data-testid="story-invite-card">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <BookOpen className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-xl font-serif font-semibold text-foreground mb-1">A story for you</h2>
        <p className="text-sm text-muted-foreground mb-1">
          <span className="font-medium text-foreground">{linkInfo.owner_name}</span> shared a story
        </p>
        {linkInfo.story_preview && (
          <p className="text-xs text-muted-foreground italic mt-2 mb-4 line-clamp-2">"{linkInfo.story_preview}"</p>
        )}
        {isLoggedIn ? (
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="w-full px-6 py-3 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            data-testid="story-invite-claim"
          >
            {claiming ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save to My Stories'}
          </button>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => { localStorage.setItem('pending_story_invite', linkId); navigate('/signup'); }}
              className="w-full px-6 py-3 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors"
              data-testid="story-invite-signup"
            >
              Sign Up to Save
            </button>
            <button
              onClick={() => { localStorage.setItem('pending_story_invite', linkId); navigate('/signup'); }}
              className="w-full px-6 py-2.5 bg-secondary text-foreground rounded-xl text-sm hover:bg-secondary/80 transition-colors"
              data-testid="story-invite-login"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
